/**
 * ClinicalNode - Nodo médico 3D interactivo
 * Representa un dato clínico en el espacio tridimensional con
 * visualización rica: mesh, glow, labels, anillos y partículas
 */

import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import {
  ClinicalNodeData,
  NodeRenderState,
  isNodeCritical,
} from '../types/medical';
import {
  MEDICAL_PHYSICS,
  VISUAL_CONFIG,
  getNodeTypeColor,
  getNodeRadius,
  TRIAGE_COLORS
} from '../core/config';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class ClinicalNode extends THREE.Group {
  // Datos clínicos
  data: ClinicalNodeData;
  
  // Elementos visuales Three.js
  mesh: THREE.Mesh;
  glow: THREE.Sprite;
  label: CSS2DObject | null = null;
  rings: THREE.Mesh[] = [];
  trail: THREE.Points | null = null;
  
  // Estado de renderizado
  renderState: NodeRenderState;
  
  // Estado físico para simulación
  velocity: THREE.Vector3;
  acceleration: THREE.Vector3;
  mass: number;
  
  // Estado clínico derivado
  isCritical: boolean;
  timeToResolution: number;
  
  // Historial de posiciones para trail
  private positionHistory: THREE.Vector3[] = [];
  private trailGeometry: THREE.BufferGeometry | null = null;
  
  // Materiales (para actualización) — públicos para que Patient pueda
  // ajustar opacity en modo focus sin romper encapsulamiento.
  baseMaterial: THREE.MeshStandardMaterial;
  glowMaterial: THREE.SpriteMaterial;

  constructor(data: ClinicalNodeData) {
    super();
    
    this.data = data;
    this.isCritical = isNodeCritical(data);
    this.timeToResolution = this.calculateTimeToResolution();
    
    // Estado inicial
    this.renderState = {
      position: new THREE.Vector3(0, 0, 0),
      velocity: new THREE.Vector3(0, 0, 0),
      acceleration: new THREE.Vector3(0, 0, 0),
      isHovered: false,
      isSelected: false,
      isExpanded: false,
      pulsePhase: Math.random() * Math.PI * 2,
      glowIntensity: this.isCritical ? VISUAL_CONFIG.GLOW_CRITICAL : VISUAL_CONFIG.GLOW_NORMAL,
    };
    
    // Física
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.acceleration = new THREE.Vector3(0, 0, 0);
    this.mass = data.physics.mass;
    
    // Construir elementos visuales
    this.baseMaterial = this.createBaseMaterial();
    this.mesh = this.createMesh();
    this.add(this.mesh);
    
    this.glowMaterial = this.createGlowMaterial();
    this.glow = this.createGlow();
    this.add(this.glow);
    
    this.setupRings();
    this.setupTrail();
    
    // Posición inicial aleatoria
    this.position.set(
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4
    );
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CREACIÓN DE ELEMENTOS VISUALES
  // ═════════════════════════════════════════════════════════════════════════════
  
  private createBaseMaterial(): THREE.MeshStandardMaterial {
    const color = this.data.visualization.baseColor || getNodeTypeColor(this.data.type);
    const emissive = this.isCritical ? TRIAGE_COLORS.immediate : 0x000000;
    
    return new THREE.MeshStandardMaterial({
      color: color,
      emissive: emissive,
      emissiveIntensity: this.isCritical ? 0.3 : 0,
      roughness: 0.3,
      metalness: 0.2,
    });
  }

  private createMesh(): THREE.Mesh {
    // Jerarquía visual: el diagnóstico es el "ancla" clínica → más grande
    // que los hallazgos que lo sustentan. Factor 1.4× además del radio
    // base del tipo.
    const dxBoost = this.data.type === 'diagnosis' ? 1.4 : 1.0;
    const radius = getNodeRadius(this.data.type) * (this.data.visualization.scale || 1) * dxBoost;
    const geometry = new THREE.SphereGeometry(radius, 32, 16);
    const mesh = new THREE.Mesh(geometry, this.baseMaterial);
    mesh.userData = { nodeId: this.data.id, node: this };
    return mesh;
  }

  private createGlowMaterial(): THREE.SpriteMaterial {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext('2d')!;
    
    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
    const baseColor = this.data.visualization.baseColor || getNodeTypeColor(this.data.type);
    
    gradient.addColorStop(0, baseColor + 'FF');
    gradient.addColorStop(0.3, baseColor + '80');
    gradient.addColorStop(0.7, baseColor + '20');
    gradient.addColorStop(1, baseColor + '00');
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, 128, 128);
    
    const texture = new THREE.CanvasTexture(canvas);
    
    return new THREE.SpriteMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      opacity: this.renderState.glowIntensity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }

  private createGlow(): THREE.Sprite {
    const sprite = new THREE.Sprite(this.glowMaterial);
    // *3 (antes *6): el glow gigante inundaba el canvas cuando había
    // 4-5 nodos cercanos. Más contenido = mejor legibilidad.
    const scale = getNodeRadius(this.data.type) * 3;
    sprite.scale.set(scale, scale, 1);
    return sprite;
  }

  private setupRings(): void {
    const ringConfigs = [
      { color: 0xff0000, radius: 1.3, condition: () => this.data.severity === 'life-threatening' },
      { color: 0x00ff00, radius: 1.4, condition: () => this.data.certainty !== undefined && this.data.certainty > 0.8 },
      { color: 0x0000ff, radius: 1.5, condition: () => this.data.physics.timeCriticality > 0.7 },
    ];
    
    ringConfigs.forEach(config => {
      const geometry = new THREE.RingGeometry(
        config.radius * 0.08,
        config.radius * 0.1,
        VISUAL_CONFIG.RING_SEGMENTS
      );
      const material = new THREE.MeshBasicMaterial({
        color: config.color,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(geometry, material);
      ring.rotation.x = Math.PI / 2;
      this.rings.push(ring);
      this.add(ring);
    });
  }

  private setupTrail(): void {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(VISUAL_CONFIG.TRAIL_LENGTH * 3);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: this.data.visualization.baseColor || getNodeTypeColor(this.data.type),
      size: 0.05,
      transparent: true,
      opacity: VISUAL_CONFIG.TRAIL_OPACITY,
    });
    
    this.trail = new THREE.Points(geometry, material);
    this.trail.visible = false;
    this.add(this.trail);
    
    this.trailGeometry = geometry;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LABELS
  // ═════════════════════════════════════════════════════════════════════════════
  
  createLabel(): CSS2DObject {
    const div = document.createElement('div');
    div.className = 'clinical-node-label';

    const severityIcons: Record<string, string> = {
      mild: '🟢', moderate: '🟡', severe: '🟠', 'life-threatening': '🔴',
    };
    const icon = this.data.severity ? (severityIcons[this.data.severity] ?? '') : '';

    // Contenido detallado que se revela al hover/select.
    let details = '';
    if (this.data.valueQuantity) {
      let flag = '';
      if (this.data.referenceRange) {
        const v = this.data.valueQuantity.value;
        if (this.data.referenceRange.high && v > this.data.referenceRange.high) flag = ' ⬆';
        else if (this.data.referenceRange.low && v < this.data.referenceRange.low) flag = ' ⬇';
      }
      details = `${this.data.valueQuantity.value} ${this.data.valueQuantity.unit}${flag}`;
    } else if (this.data.valueString) {
      details = this.data.valueString.slice(0, 80);
    } else if (this.data.valueBoolean !== undefined) {
      details = this.data.valueBoolean ? '✓ Sí' : '✗ No';
    }

    // Estructura: siempre se muestra compact (icono + nombre); details se
    // oculta con CSS y se revela cuando el label tiene clase .expanded.
    div.innerHTML = `
      <span class="cnl-compact">${icon}${icon ? ' ' : ''}${this.data.concept.display}</span>
      ${details ? `<span class="cnl-details">${details}</span>` : ''}
    `;

    const label = new CSS2DObject(div);
    label.position.set(0, getNodeRadius(this.data.type) * 2.2, 0);
    label.visible = false;

    this.label = label;
    this.add(label);

    return label;
  }

  /**
   * Sincroniza el estado visual del label con hover/selected. Se llama
   * cada frame desde Patient.update(). Cuando el nodo está activo, el CSS
   * `.expanded` revela la línea de detalles.
   */
  updateLabelState(isHovered: boolean, isSelected: boolean): void {
    if (!this.label) return;
    const el = this.label.element;
    const expanded = isHovered || isSelected;
    el.classList.toggle('expanded', expanded);
    if (isSelected) el.classList.add('selected'); else el.classList.remove('selected');
  }

  updateLabel(): void {
    if (!this.label) return;
    
    const div = this.label.element as HTMLDivElement;
    let content = `<strong>${this.data.concept.display}</strong>`;
    
    if (this.renderState.isExpanded) {
      // Mostrar más información cuando está expandido
      if (this.data.valueQuantity) {
        content += `<br/>${this.data.valueQuantity.value} ${this.data.valueQuantity.unit}`;
        if (this.data.referenceRange) {
          content += `<br/>Ref: ${this.data.referenceRange.low || '-'} - ${this.data.referenceRange.high || '-'} ${this.data.valueQuantity.unit}`;
        }
      }
      if (this.data.note) {
        content += `<br/><em>${this.data.note}</em>`;
      }
    } else {
      if (this.data.valueQuantity) {
        content += `<br/>${this.data.valueQuantity.value} ${this.data.valueQuantity.unit}`;
      }
    }
    
    div.innerHTML = content;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // ACTUALIZACIÓN POR FRAME
  // ═════════════════════════════════════════════════════════════════════════════
  
  update(deltaTime: number, globalTime: number): void {
    // Actualizar física
    this.updatePhysics(deltaTime);
    
    // Actualizar visuales
    this.updateVisuals(globalTime);
    
    // Verificar estado crítico
    this.checkCriticalStatus();
    
    // Decaimiento temporal
    this.updateTemporalDecay(deltaTime);
    
    // Actualizar trail
    this.updateTrail();
    
    // Sincronizar estado de renderizado
    this.renderState.position.copy(this.position);
    this.renderState.velocity.copy(this.velocity);
  }

  private updatePhysics(deltaTime: number): void {
    // Aplicar aceleración a velocidad
    this.velocity.add(this.acceleration.clone().multiplyScalar(deltaTime));
    
    // Limitar velocidad máxima
    const speed = this.velocity.length();
    if (speed > MEDICAL_PHYSICS.MAX_VELOCITY) {
      this.velocity.normalize().multiplyScalar(MEDICAL_PHYSICS.MAX_VELOCITY);
    }
    
    // Aplicar fricción
    this.velocity.multiplyScalar(MEDICAL_PHYSICS.FRICTION);
    
    // Aplicar velocidad a posición
    this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
    
    // Resetear aceleración
    this.acceleration.set(0, 0, 0);
    
    // Limitar a boundary
    const distFromCenter = this.position.length();
    if (distFromCenter > MEDICAL_PHYSICS.BOUNDARY_RADIUS) {
      const pushBack = this.position.clone().normalize().multiplyScalar(-0.1);
      this.velocity.add(pushBack);
    }
  }

  private updateVisuals(_globalTime: number): void {
    // Animación de pulso. El diagnóstico SIEMPRE pulsa sutilmente: es la
    // pieza central del caso y queremos que el alumno lo identifique a
    // primera vista.
    const isDx = this.data.type === 'diagnosis';
    if (this.data.visualization.pulse || this.isCritical || isDx) {
      const speed = this.isCritical
        ? VISUAL_CONFIG.PULSE_SPEED_CRITICAL
        : (isDx ? VISUAL_CONFIG.PULSE_SPEED_NORMAL * 0.6 : VISUAL_CONFIG.PULSE_SPEED_NORMAL);
      this.renderState.pulsePhase += 0.05 * speed;

      const pulseAmp = isDx && !this.isCritical ? 0.06 : 0.1;
      const pulseScale = 1 + Math.sin(this.renderState.pulsePhase) * pulseAmp;
      this.mesh.scale.setScalar(pulseScale);

      // Glow: dx tiene glow base más alto que otros para destacarlo
      const baseGlow = this.isCritical
        ? VISUAL_CONFIG.GLOW_CRITICAL
        : (isDx ? VISUAL_CONFIG.GLOW_NORMAL * 1.6 : VISUAL_CONFIG.GLOW_NORMAL);
      this.renderState.glowIntensity = baseGlow + Math.sin(this.renderState.pulsePhase) * 0.2;
      this.glowMaterial.opacity = this.renderState.glowIntensity;
    }
    
    // Actualizar anillos
    this.rings.forEach((ring, index) => {
      const material = ring.material as THREE.MeshBasicMaterial;
      const targetOpacity = this.renderState.isHovered || this.renderState.isSelected ? 0.6 : 0.2;
      material.opacity = THREE.MathUtils.lerp(material.opacity, targetOpacity, 0.1);
      
      // Rotación de anillos
      ring.rotation.z += 0.01 * (index + 1);
    });
    
    // Actualizar material si está seleccionado
    if (this.renderState.isSelected) {
      this.baseMaterial.emissiveIntensity = THREE.MathUtils.lerp(
        this.baseMaterial.emissiveIntensity,
        0.8,
        0.1
      );
    } else {
      const targetEmissive = this.isCritical ? 0.3 : 0;
      this.baseMaterial.emissiveIntensity = THREE.MathUtils.lerp(
        this.baseMaterial.emissiveIntensity,
        targetEmissive,
        0.1
      );
    }
  }

  checkCriticalStatus(): void {
    const wasCritical = this.isCritical;
    this.isCritical = isNodeCritical(this.data);
    
    if (this.isCritical && !wasCritical) {
      // Acaba de volverse crítico
      this.data.visualization.pulse = true;
    }
  }

  private updateTemporalDecay(deltaTime: number): void {
    if (this.data.physics.decayHalfLife && this.timeToResolution > 0) {
      this.timeToResolution -= deltaTime;
      if (this.timeToResolution <= 0) {
        this.timeToResolution = 0;
        // El nodo ha "expirado" (ej: medicamento)
        this.baseMaterial.opacity = 0.3;
      }
    }
  }

  private updateTrail(): void {
    if (!this.trail || !this.trailGeometry) return;
    
    // Agregar posición actual al historial
    this.positionHistory.unshift(this.position.clone());
    if (this.positionHistory.length > VISUAL_CONFIG.TRAIL_LENGTH) {
      this.positionHistory.pop();
    }
    
    // Actualizar geometría
    const positions = this.trailGeometry.attributes.position.array as Float32Array;
    for (let i = 0; i < this.positionHistory.length; i++) {
      positions[i * 3] = this.positionHistory[i].x;
      positions[i * 3 + 1] = this.positionHistory[i].y;
      positions[i * 3 + 2] = this.positionHistory[i].z;
    }
    this.trailGeometry.attributes.position.needsUpdate = true;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // ACCIONES
  // ═════════════════════════════════════════════════════════════════════════════
  
  pulse(): void {
    // Animación de pulso manual
    const originalScale = getNodeRadius(this.data.type);
    
    // Animar escala
    const duration = 500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const scale = originalScale * (1 + Math.sin(progress * Math.PI) * 0.5);
      this.mesh.scale.setScalar(scale / originalScale);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.mesh.scale.setScalar(1);
      }
    };
    
    animate();
  }

  /**
   * Efecto visual al revelar un nodo nuevo: pulse amplificado + un anillo
   * plano que se expande y desvanece. Llama la atención al alumno cuando
   * un examen ordenado trae datos o un gesto revela un hallazgo físico.
   */
  revealBurst(): void {
    this.pulse();

    const baseRadius = getNodeRadius(this.data.type);
    const color = new THREE.Color(this.data.visualization.baseColor || '#2997FF');
    const ringGeo = new THREE.RingGeometry(baseRadius * 1.2, baseRadius * 1.5, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    // Orientar hacia la cámara: aprox. plano XY.
    ring.rotation.x = Math.PI / 2;
    this.add(ring);

    const start = Date.now();
    const duration = 900;
    const animate = () => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const scale = 1 + t * 6;
      ring.scale.setScalar(scale);
      ringMat.opacity = 0.85 * (1 - t);
      if (t < 1) requestAnimationFrame(animate);
      else {
        this.remove(ring);
        ringGeo.dispose();
        ringMat.dispose();
      }
    };
    animate();
  }

  setCritical(isCritical: boolean): void {
    this.isCritical = isCritical;
    this.data.visualization.pulse = isCritical;
    
    if (isCritical) {
      this.baseMaterial.emissive.setHex(TRIAGE_COLORS.immediate);
      this.data.severity = 'life-threatening';
    } else {
      this.baseMaterial.emissive.setHex(0x000000);
    }
  }

  showTrend(direction: 'improving' | 'worsening' | 'stable'): void {
    // Cambiar color temporalmente según tendencia
    const trendColors = {
      improving: 0x22C55E,
      worsening: 0xDC2626,
      stable: 0x6B7280
    };
    
    const originalColor = this.baseMaterial.color.clone();
    this.baseMaterial.color.setHex(trendColors[direction]);
    
    // Restaurar después de 2 segundos
    setTimeout(() => {
      this.baseMaterial.color.copy(originalColor);
    }, 2000);
  }

  explode(): void {
    // Efecto de explosión cuando se confirma diagnóstico
    const particleCount = 20;
    const particles: THREE.Mesh[] = [];
    
    for (let i = 0; i < particleCount; i++) {
      const geometry = new THREE.SphereGeometry(0.02, 8, 8);
      const material = new THREE.MeshBasicMaterial({
        color: this.data.visualization.baseColor || getNodeTypeColor(this.data.type),
        transparent: true,
      });
      const particle = new THREE.Mesh(geometry, material);
      
      particle.position.copy(this.position);
      particle.userData.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2
      );
      
      this.parent?.add(particle);
      particles.push(particle);
    }
    
    // Animar partículas
    const animateParticles = () => {
      let active = false;
      
      particles.forEach(p => {
        p.position.add(p.userData.velocity.clone().multiplyScalar(0.016));
        p.userData.velocity.multiplyScalar(0.95);
        (p.material as THREE.MeshBasicMaterial).opacity -= 0.02;
        
        if ((p.material as THREE.MeshBasicMaterial).opacity > 0) {
          active = true;
        }
      });
      
      if (active) {
        requestAnimationFrame(animateParticles);
      } else {
        particles.forEach(p => {
          p.geometry.dispose();
          (p.material as THREE.MeshBasicMaterial).dispose();
          p.parent?.remove(p);
        });
      }
    };
    
    animateParticles();
  }

  expand(): void {
    this.renderState.isExpanded = true;
    this.updateLabel();
    this.trail && (this.trail.visible = true);
  }

  collapse(): void {
    this.renderState.isExpanded = false;
    this.updateLabel();
    this.trail && (this.trail.visible = false);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════
  
  private calculateTimeToResolution(): number {
    if (this.data.physics.decayHalfLife) {
      return this.data.physics.decayHalfLife;
    }
    if (this.data.physics.timeCriticality > 0) {
      return 3600 / this.data.physics.timeCriticality; // 1 hora / criticidad
    }
    return Infinity;
  }

  applyForce(force: THREE.Vector3): void {
    // F = ma, entonces a = F/m
    const acceleration = force.clone().divideScalar(this.mass);
    this.acceleration.add(acceleration);
  }

  getBoundingRadius(): number {
    return getNodeRadius(this.data.type);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LIMPIEZA
  // ═════════════════════════════════════════════════════════════════════════════
  
  dispose(): void {
    this.mesh.geometry.dispose();
    this.baseMaterial.dispose();
    
    this.glowMaterial.map?.dispose();
    this.glowMaterial.dispose();
    
    this.rings.forEach(ring => {
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
    });
    
    if (this.trail) {
      this.trailGeometry?.dispose();
      (this.trail.material as THREE.Material).dispose();
    }
    
    if (this.label) {
      this.label.element.remove();
    }
  }
}

export default ClinicalNode;
